import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Calendar, Clock, MapPin, Video, ExternalLink, Trash2, Edit2,
  ChevronDown, ChevronRight, Sparkles, FileText, Loader2, Download,
  Link2, CheckCircle2, Search, Users, Briefcase, FolderKanban,
  User, Mail, Phone, Check, Ban, AlertCircle, RefreshCw, ChevronLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { CalendarEvent } from '../../lib/types';
import type { MeetingRecording } from '../../lib/types/meetingRecording';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useMeetingRecordingStore } from '../../lib/stores/meetingRecordingStore';
import { useProjectStatusUpdateStore } from '../../lib/stores/projectStatusUpdateStore';
import { useBookingStore } from '../../lib/stores/bookingStore';
import { BOOKING_STATUS_CONFIG } from '../../lib/types/booking';
import type { BookingSlot } from '../../lib/types/booking';
import { BookerCalendar } from '../booking-public/BookerCalendar';
import { ResponsiveModal } from '../ui/ResponsiveModal';
import { supabase } from '../../lib/supabase';
import { EXCLUDE_REFERENTI } from '../../lib/utils/contactPollution';
import { usePermissions } from '../../lib/hooks/usePermissions';
import { EventOwnerRows, SalesActivityQuickOutcome } from './SalesActivityQuickOutcome';
import { SanitizedHtml } from '../mail/SanitizedHtml';

interface CalendarEventDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  event: CalendarEvent | null;
  onEdit?: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
  canDelete?: boolean;
  translations?: any;
  onBookingAction?: () => void;
  /** Riprogramma un evento Google in Flusia (come evento o prenotazione). */
  onRescheduleToFlusia?: (event: CalendarEvent) => void;
  /** Esito rapido salvato su una task commerciale: ricarica il calendario e chiude. */
  onOutcomeSaved?: () => void;
}

// ─── Notion-style property row ────────────────────────────────
const PropertyRow: React.FC<{
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}> = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center gap-2 px-2 py-[7px] rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] transition-colors">
      <div className="flex items-center gap-2 w-[120px] flex-shrink-0">
        <Icon className="w-4 h-4 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
        <span className="text-[13px] text-[#787774] dark:text-gray-500">{label}</span>
      </div>
      <div className="flex-1 min-w-0 text-[13px] text-[#37352F] dark:text-[#FFFFFFCF]">
        {value}
      </div>
    </div>
  );
};

// ─── Status config (same as MeetingRecordingCard) ────────────
// Label del link meeting condizionale sul dominio dell'URL: il campo meet_link
// può contenere sia Google Meet che Microsoft Teams.
const getMeetingLinkLabel = (url: string): string => {
  if (url.includes('teams.microsoft.com')) return 'Microsoft Teams';
  if (url.includes('meet.google.com')) return 'Google Meet';
  return 'Link meeting';
};

const statusConfig: Record<string, { label: string; color: string }> = {
  pending: { label: 'In attesa', color: 'text-yellow-600 bg-yellow-500/10' },
  joining: { label: 'In accesso', color: 'text-blue-600 bg-blue-500/10' },
  active: { label: 'Registrando', color: 'text-red-600 bg-red-500/10' },
  completed: { label: 'Completato', color: 'text-green-600 bg-green-500/10' },
  failed: { label: 'Errore', color: 'text-red-600 bg-red-500/10' },
  stopped: { label: 'Fermato', color: 'text-gray-600 bg-gray-500/10' },
};

type EntityTab = 'client' | 'deal' | 'project';

export function CalendarEventDrawer({
  isOpen,
  onClose,
  event,
  onEdit,
  onDelete,
  canEdit = true,
  canDelete = true,
  translations: t = {},
  onBookingAction,
  onRescheduleToFlusia,
  onOutcomeSaved,
}: CalendarEventDrawerProps) {

  const navigate = useNavigate();

  // ─── Client info for linked events ──────────────────────────
  const [linkedClient, setLinkedClient] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (!event?.client_id) {
      setLinkedClient(null);
      return;
    }
    supabase
      .from('clients')
      .select('id, business_name')
      .eq('id', event.client_id)
      .single()
      .then(({ data }) => {
        setLinkedClient(data ? { id: data.id, name: data.business_name } : null);
      })
      .catch(() => setLinkedClient(null));
  }, [event?.client_id]);

  // ─── Recording state ───────────────────────────────────────
  const [recording, setRecording] = useState<MeetingRecording | null>(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState<'summary' | 'transcript' | null>(null);
  const [fetchingTranscript, setFetchingTranscript] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);

  // ─── Booking state ──────────────────────────────────────────
  const [bookingActionLoading, setBookingActionLoading] = useState(false);
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleStep, setRescheduleStep] = useState<'date' | 'time'>('date');
  const [rescheduleDate, setRescheduleDate] = useState<string | null>(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  // Orario manuale (fuori griglia) per la riprogrammazione
  const [manualRescheduleTime, setManualRescheduleTime] = useState('');
  const [ignoreConflict, setIgnoreConflict] = useState(false);
  // Durata manuale (min) per la riprogrammazione — default = durata attuale del booking
  const [manualRescheduleDuration, setManualRescheduleDuration] = useState('');
  const [showDayBookings, setShowDayBookings] = useState(false);

  const {
    confirmBooking, cancelBooking, completeBooking,
    markNoShow, rescheduleBooking, rejectBooking,
    fetchPublicSlots, publicSlots, publicAllSlots, publicBusyIntervals, publicDayBookings, publicSlotsLoading,
  } = useBookingStore();

  // ─── Association state ─────────────────────────────────────
  const [showAssociation, setShowAssociation] = useState(false);
  const [activeTab, setActiveTab] = useState<EntityTab>('client');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [linkSuccess, setLinkSuccess] = useState<string | null>(null);

  const {
    fetchRecordingByCalendarEventId,
    fetchTranscript,
    generateSummary,
    linkRecordingToEntity,
  } = useMeetingRecordingStore();

  const { createStatusUpdate } = useProjectStatusUpdateStore();
  const { canEditBooking, canDeleteBooking } = usePermissions();

  // ─── Escape key handler ────────────────────────────────────
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // ─── Reset booking state on event change ──────────────────
  useEffect(() => {
    if (!isOpen || !event) {
      setShowCancelReason(false);
      setCancelReason('');
      setShowRescheduleModal(false);
      setRescheduleStep('date');
      setRescheduleDate(null);
    }
  }, [isOpen, event?.id]);

  // ─── Fetch recording when event changes ────────────────────
  useEffect(() => {
    if (!isOpen || !event) {
      setRecording(null);
      setExpandedSection(null);
      setShowAssociation(false);
      setLinkSuccess(null);
      return;
    }

    const isGoogle = event.source === 'google';
    if (!isGoogle) return;

    const calendarEventId = event.id.replace('google-', '');
    const meetingUrl = event.conference_data?.entryPoints?.find(
      (ep: any) => ep.entryPointType === 'video'
    )?.uri;

    setRecordingLoading(true);
    fetchRecordingByCalendarEventId(calendarEventId, meetingUrl || undefined)
      .then(rec => setRecording(rec))
      .finally(() => setRecordingLoading(false));
  }, [isOpen, event?.id]);

  // ─── Search entities ───────────────────────────────────────
  const searchEntities = useCallback(async (query: string, tab: EntityTab) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!profile?.organization_id) return;

      let results: any[] = [];

      if (tab === 'client') {
        const { data } = await supabase
          .from('clients')
          .select('id, business_name, trade_name, email, entity_type')
          .eq('organization_id', profile.organization_id)
          .or(`business_name.ilike.%${query}%, trade_name.ilike.%${query}%, email.ilike.%${query}%`)
          .or(EXCLUDE_REFERENTI)
          .limit(10);
        results = (data || []).map(c => ({
          id: c.id,
          label: c.business_name || c.trade_name || c.email,
          sub: c.entity_type === 'lead' ? 'Lead' : 'Cliente',
          entityType: c.entity_type,
        }));
      } else if (tab === 'deal') {
        const { data } = await supabase
          .from('deals')
          .select('id, title, amount, client:clients(business_name)')
          .eq('organization_id', profile.organization_id)
          .ilike('title', `%${query}%`)
          .limit(10);
        results = (data || []).map((d: any) => ({
          id: d.id,
          label: d.title,
          sub: d.client?.business_name || (d.amount ? `${d.amount.toLocaleString('it-IT')}` : ''),
        }));
      } else if (tab === 'project') {
        const { data } = await supabase
          .from('projects')
          .select('id, name, status')
          .eq('organization_id', profile.organization_id)
          .ilike('name', `%${query}%`)
          .limit(10);
        results = (data || []).map(p => ({
          id: p.id,
          label: p.name,
          sub: p.status,
        }));
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      searchEntities(searchQuery, activeTab);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery, activeTab, searchEntities]);

  // Reset search when tab changes
  useEffect(() => {
    setSearchQuery('');
    setSearchResults([]);
  }, [activeTab]);

  // ─── Handle link ───────────────────────────────────────────
  const handleLink = async (entityId: string, tab: EntityTab) => {
    if (!recording) return;
    setLinking(true);

    try {
      if (tab === 'client') {
        const selected = searchResults.find(r => r.id === entityId);
        if (selected?.entityType === 'lead') {
          await linkRecordingToEntity(recording.id, { lead_id: entityId });
        } else {
          await linkRecordingToEntity(recording.id, { client_id: entityId });
        }
        setLinkSuccess(`Associato a ${selected?.label}`);
      } else if (tab === 'deal') {
        await linkRecordingToEntity(recording.id, { deal_id: entityId });
        const selected = searchResults.find(r => r.id === entityId);
        setLinkSuccess(`Associato a ${selected?.label}`);
      } else if (tab === 'project') {
        const keyPointsHtml = recording.ai_key_points.length > 0
          ? `<h3>Punti chiave</h3><ul>${recording.ai_key_points.map(p => `<li>${p}</li>`).join('')}</ul>`
          : '';

        const description = [
          `<p><strong>Riunione: ${recording.meeting_title || 'Meeting'}</strong></p>`,
          recording.ai_summary ? `<p>${recording.ai_summary}</p>` : '',
          keyPointsHtml,
        ].filter(Boolean).join('');

        const nextSteps = recording.ai_action_items.length > 0
          ? `<ul>${recording.ai_action_items.map(a => `<li>${a.text}${a.assignee ? ` (${a.assignee})` : ''}</li>`).join('')}</ul>`
          : undefined;

        await createStatusUpdate(
          {
            project_id: entityId,
            description: description || '<p>Registrazione meeting associata</p>',
            health_status: 'on_track',
            completion_percentage: 0,
            next_steps: nextSteps,
          },
          []
        );

        const selected = searchResults.find(r => r.id === entityId);
        setLinkSuccess(`Status update creato in ${selected?.label}`);
      }

      setShowAssociation(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      console.error('Error linking recording:', err);
    } finally {
      setLinking(false);
    }
  };

  // ─── Handlers ──────────────────────────────────────────────
  const handleFetchTranscript = async () => {
    if (!recording) return;
    setFetchingTranscript(true);
    const ok = await fetchTranscript(recording.id);
    if (ok) {
      const calendarEventId = event!.id.replace('google-', '');
      const meetingUrl = event?.conference_data?.entryPoints?.find(
        (ep: any) => ep.entryPointType === 'video'
      )?.uri;
      const updated = await fetchRecordingByCalendarEventId(calendarEventId, meetingUrl || undefined);
      if (updated) setRecording(updated);
    }
    setFetchingTranscript(false);
  };

  const handleGenerateSummary = async () => {
    if (!recording) return;
    setGeneratingSummary(true);
    const ok = await generateSummary(recording.id);
    if (ok) {
      const calendarEventId = event!.id.replace('google-', '');
      const meetingUrl = event?.conference_data?.entryPoints?.find(
        (ep: any) => ep.entryPointType === 'video'
      )?.uri;
      const updated = await fetchRecordingByCalendarEventId(calendarEventId, meetingUrl || undefined);
      if (updated) setRecording(updated);
    }
    setGeneratingSummary(false);
  };

  // ─── Booking action helpers ───────────────────────────────
  const isBookingEvent = event?.event_type === 'booking';
  const bookingId = event?.metadata?.booking_id;
  const bookingStatus = event?.metadata?.status as string | undefined;
  const isConfirmedBooking = isBookingEvent && bookingStatus === 'confirmed';
  const isPendingBooking = isBookingEvent && bookingStatus === 'pending';
  const isCancelledBooking = isBookingEvent && bookingStatus === 'cancelled';
  const isPastBooking = isBookingEvent && event && new Date(event.start_time) < new Date();

  const handleBookingAction = async (action: () => Promise<boolean>) => {
    setBookingActionLoading(true);
    const ok = await action();
    setBookingActionLoading(false);
    if (ok) {
      onBookingAction?.();
      onClose();
    }
  };

  const handleRescheduleDate = (dateStr: string) => {
    setRescheduleDate(dateStr);
    const eventTypeId = event?.metadata?.event_type_id;
    if (eventTypeId) {
      fetchPublicSlots(eventTypeId, dateStr, Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
    setRescheduleStep('time');
  };

  const handleRescheduleSlot = async (slot: BookingSlot, forceOverride?: boolean) => {
    if (!bookingId) return;
    setRescheduling(true);
    setRescheduleError(null);
    const res = await rescheduleBooking(bookingId, slot.start, slot.end, forceOverride);
    setRescheduling(false);
    if (res.success) {
      setShowRescheduleModal(false);
      onBookingAction?.();
      onClose();
    } else {
      setRescheduleError(res.error || 'Riprogrammazione non riuscita');
    }
  };

  // ─── Format helpers ────────────────────────────────────────
  const formatEventDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return format(date, "EEEE d MMMM yyyy", { locale: it });
  };

  const formatEventTime = (startTime: string | Date, endTime: string | Date, allDay?: boolean) => {
    if (allDay) {
      return t.allDay || 'Tutto il giorno';
    }
    const start = new Date(startTime);
    const end = new Date(endTime);
    return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
  };

  const isGoogleEvent = event?.source === 'google';
  const isAlfadocsEvent = event?.source === 'alfadocs';
  const alfadocsPracticeName = (event?.metadata as any)?.alfadocs_practice_name as string | undefined;

  // Color band: booking pending = amber, google = blue, otherwise event color or default
  const coverColor = isBookingEvent && bookingStatus === 'pending'
    ? '#F59E0B'
    : isGoogleEvent
      ? '#2383E2'
      : event?.color || '#787774';

  const tabConfig: { key: EntityTab; label: string; icon: React.ElementType }[] = [
    { key: 'client', label: 'Cliente', icon: Users },
    { key: 'deal', label: 'Deal', icon: Briefcase },
    { key: 'project', label: 'Progetto', icon: FolderKanban },
  ];

  return (
    <AnimatePresence mode="wait">
      {isOpen && event && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/20 dark:bg-black/40 z-[10000]"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
            className="fixed inset-y-0 right-0 w-[420px] max-w-full bg-white dark:bg-[#2F3437] border-l border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] shadow-xl z-[10001] flex flex-col"
          >
            {/* ── Color Cover Band ── */}
            <div className="h-2 flex-shrink-0" style={{ backgroundColor: coverColor }} />

            {/* ── Toolbar ── */}
            <div className="flex items-center justify-between px-5 pt-3 pb-1">
              <div className="flex items-center gap-1.5">
                {isGoogleEvent && (
                  <span className="px-2 py-0.5 bg-[rgba(35,131,226,0.1)] text-[#2383E2] text-[10px] font-medium rounded-sm">
                    Google
                  </span>
                )}
                {isBookingEvent && bookingStatus && (
                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded-sm ${BOOKING_STATUS_CONFIG[bookingStatus as keyof typeof BOOKING_STATUS_CONFIG]?.bgColor || 'bg-gray-100'} ${BOOKING_STATUS_CONFIG[bookingStatus as keyof typeof BOOKING_STATUS_CONFIG]?.color || 'text-gray-600'}`}>
                    Prenotazione
                  </span>
                )}
                {isAlfadocsEvent && (
                  <span className="px-2 py-0.5 bg-[rgba(16,185,129,0.1)] text-emerald-600 dark:text-emerald-400 text-[10px] font-medium rounded-sm">
                    AlfaDocs
                  </span>
                )}
                {isAlfadocsEvent && alfadocsPracticeName && (
                  <span className="px-2 py-0.5 bg-[rgba(16,185,129,0.1)] text-emerald-600 dark:text-emerald-400 text-[10px] font-medium rounded-sm">
                    {alfadocsPracticeName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-0.5">
                {!isGoogleEvent && !isBookingEvent && canEdit && onEdit && !((event.metadata as any)?.alfadocs_state === 'cancelled') && (
                  <button
                    onClick={onEdit}
                    className="p-1.5 rounded-md text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 hover:text-[#2383E2] transition-colors"
                    title={t.edit || 'Modifica'}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                )}
                {!isGoogleEvent && !isBookingEvent && canDelete && onDelete && !((event.metadata as any)?.alfadocs_state === 'cancelled') && (
                  <button
                    onClick={onDelete}
                    className="p-1.5 rounded-md text-[#787774] dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 transition-colors"
                    title={t.delete || 'Elimina'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* ── Title (page-like) ── */}
              <div className="px-5 pt-2 pb-4">
                <h2 className="text-[22px] font-bold text-[#37352F] dark:text-white leading-tight">
                  {event.title}
                </h2>
                {(event.metadata as any)?.alfadocs_state === 'cancelled' && (
                  <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-md">
                    Cancellato su AlfaDocs
                  </span>
                )}
                {event.source_name && isGoogleEvent && (
                  <p className="text-xs text-[#787774] dark:text-gray-500 mt-1">
                    {t.from || 'Da'}: {event.source_name}
                  </p>
                )}
              </div>

              {/* ── Calendario di, fissato da, esito rapido (task commerciali) ── */}
              {(event.source as string) === 'sales_activities' && (
                <SalesActivityQuickOutcome event={event} canEdit={canEdit} onDone={() => onOutcomeSaved?.()} onReschedule={onEdit} />
              )}
              {(event.source as string) !== 'sales_activities' && (event.metadata as any)?.calendar_owner_id && (
                <EventOwnerRows event={event} />
              )}

              {/* ── Property Table ── */}
              <div className="px-3 pb-2">
                <PropertyRow
                  icon={Calendar}
                  label={t.date || "Data"}
                  value={<span className="capitalize">{formatEventDate(event.start_time)}</span>}
                />
                <PropertyRow
                  icon={Clock}
                  label={t.time || "Orario"}
                  value={formatEventTime(event.start_time, event.end_time, event.all_day)}
                />
                {event.location && (
                  <PropertyRow
                    icon={MapPin}
                    label={t.location || "Luogo"}
                    value={event.location}
                  />
                )}
                {linkedClient && (
                  <PropertyRow
                    icon={Users}
                    label="Cliente"
                    value={
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(`/clients/${linkedClient.id}`, '_blank');
                        }}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border transition-colors bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                        title={`Apri scheda ${linkedClient.name}`}
                      >
                        <span className="truncate max-w-[200px]">{linkedClient.name}</span>
                        <ExternalLink className="w-2.5 h-2.5 opacity-50" />
                      </button>
                    }
                  />
                )}
                {isBookingEvent && bookingStatus && (
                  <PropertyRow
                    icon={AlertCircle}
                    label="Stato"
                    value={
                      <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${BOOKING_STATUS_CONFIG[bookingStatus as keyof typeof BOOKING_STATUS_CONFIG]?.bgColor || ''} ${BOOKING_STATUS_CONFIG[bookingStatus as keyof typeof BOOKING_STATUS_CONFIG]?.color || ''}`}>
                        {BOOKING_STATUS_CONFIG[bookingStatus as keyof typeof BOOKING_STATUS_CONFIG]?.label || bookingStatus}
                      </span>
                    }
                  />
                )}
                {isBookingEvent && event?.metadata?.assigned_user_name && (
                  <PropertyRow
                    icon={Users}
                    label="Assegnato a"
                    value={event.metadata.assigned_user_name}
                  />
                )}
              </div>

              {/* ── Divider ── */}
              <div className="mx-5 border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]" />

              {/* ═══ Booking Section ═══ */}
              {isBookingEvent && event.metadata && (
                <div className="px-5 py-4 space-y-4">
                  {/* Booker Info */}
                  <div className="space-y-1">
                    <div className="text-[13px] text-[#787774] dark:text-gray-500 mb-2">
                      Partecipante
                    </div>
                    <div className="p-3 bg-[#F7F6F3] dark:bg-[#ffffff08] rounded-lg space-y-2">
                      {event.metadata.booker_name && (
                        <div className="flex items-center gap-2.5">
                          <User className="w-4 h-4 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                          <span className="text-[13px] text-[#37352F] dark:text-[#FFFFFFCF] font-medium">{event.metadata.booker_name}</span>
                        </div>
                      )}
                      {event.metadata.booker_email && (
                        <div className="flex items-center gap-2.5">
                          <Mail className="w-4 h-4 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                          <a href={`mailto:${event.metadata.booker_email}`} className="text-[13px] text-[#2383E2] hover:underline">
                            {event.metadata.booker_email}
                          </a>
                        </div>
                      )}
                      {event.metadata.booker_phone && (
                        <div className="flex items-center gap-2.5">
                          <Phone className="w-4 h-4 text-[#787774] dark:text-gray-500" strokeWidth={1.5} />
                          <a href={`tel:${event.metadata.booker_phone}`} className="text-[13px] text-[#2383E2] hover:underline">
                            {event.metadata.booker_phone}
                          </a>
                        </div>
                      )}
                      {event.metadata.meet_link && (
                        <div className="flex items-center gap-2.5">
                          <Video className="w-4 h-4 text-green-600" strokeWidth={1.5} />
                          <a href={event.metadata.meet_link} target="_blank" rel="noopener noreferrer" className="text-[13px] text-[#2383E2] hover:underline">
                            {getMeetingLinkLabel(String(event.metadata.meet_link))}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cancel reason input */}
                  {showCancelReason && canDeleteBooking && (
                    <div className="space-y-2">
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        placeholder="Motivo della cancellazione (opzionale)"
                        className="w-full border border-[rgba(55,53,47,0.16)] dark:border-[#4F5457] rounded-lg px-3 py-2 bg-white dark:bg-[#3F4447] text-[13px] resize-none focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white placeholder:text-[#787774]"
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => { setShowCancelReason(false); setCancelReason(''); }}
                          className="px-3 py-1.5 text-xs rounded-md border border-[rgba(55,53,47,0.16)] dark:border-[#4F5457] text-[#37352F] dark:text-gray-300 hover:bg-[rgba(55,53,47,0.04)] transition-colors"
                        >
                          Annulla
                        </button>
                        <button
                          onClick={() => handleBookingAction(async () => {
                            const ok = await cancelBooking(bookingId!, cancelReason || undefined);
                            setShowCancelReason(false);
                            setCancelReason('');
                            return ok;
                          })}
                          disabled={bookingActionLoading}
                          className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {bookingActionLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Conferma cancellazione'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Booking Actions */}
                  {bookingId && !showCancelReason && (canEditBooking || canDeleteBooking) && (
                    <div className="flex flex-wrap gap-2">
                      {isPendingBooking && (
                        <>
                          {canEditBooking && (
                            <button
                              onClick={() => handleBookingAction(() => confirmBooking(bookingId))}
                              disabled={bookingActionLoading}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                            >
                              {bookingActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                              Conferma
                            </button>
                          )}
                          {canDeleteBooking && (
                            <button
                              onClick={() => handleBookingAction(() => rejectBooking(bookingId))}
                              disabled={bookingActionLoading}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 text-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                            >
                              {bookingActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                              Rifiuta
                            </button>
                          )}
                        </>
                      )}
                      {isConfirmedBooking && (
                        <>
                          {canEditBooking && (
                            <>
                              <button
                                onClick={() => handleBookingAction(() => completeBooking(bookingId))}
                                disabled={bookingActionLoading}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
                              >
                                {bookingActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                                Completata
                              </button>
                              <button
                                onClick={() => setShowRescheduleModal(true)}
                                disabled={bookingActionLoading}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[rgba(55,53,47,0.16)] dark:border-[#4F5457] text-[#37352F] dark:text-gray-300 rounded-md hover:bg-[rgba(55,53,47,0.04)] disabled:opacity-50 transition-colors"
                              >
                                <RefreshCw className="w-3.5 h-3.5" />
                                Riprogramma
                              </button>
                              {isPastBooking && (
                                <button
                                  onClick={() => handleBookingAction(() => markNoShow(bookingId))}
                                  disabled={bookingActionLoading}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[rgba(55,53,47,0.16)] dark:border-[#4F5457] text-[#37352F] dark:text-gray-300 rounded-md hover:bg-[rgba(55,53,47,0.04)] disabled:opacity-50 transition-colors"
                                >
                                  {bookingActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertCircle className="w-3.5 h-3.5" />}
                                  No show
                                </button>
                              )}
                            </>
                          )}
                          {canDeleteBooking && (
                            <button
                              onClick={() => setShowCancelReason(true)}
                              disabled={bookingActionLoading}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 dark:border-red-800 text-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                            >
                              {bookingActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                              Cancella
                            </button>
                          )}
                        </>
                      )}
                      {isCancelledBooking && canEditBooking && (
                        <button
                          onClick={() => setShowRescheduleModal(true)}
                          disabled={bookingActionLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[rgba(55,53,47,0.16)] dark:border-[#4F5457] text-[#37352F] dark:text-gray-300 rounded-md hover:bg-[rgba(55,53,47,0.04)] disabled:opacity-50 transition-colors"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          Riprogramma
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ═══ Google Meet Section ═══ */}
              {(event.conference_data?.entryPoints?.length ?? 0) > 0 && (
                <div className="px-5 py-3">
                  <div className="p-3 bg-[#F7F6F3] dark:bg-[#ffffff08] rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Video className="w-4 h-4 text-green-600" />
                      <span className="text-[13px] font-medium text-[#37352F] dark:text-white">
                        Google Meet
                      </span>
                    </div>
                    <div className="space-y-1">
                      {event.conference_data!.entryPoints!.map((entry: any, index: number) => {
                        if (entry.entryPointType === 'video') {
                          return (
                            <a
                              key={index}
                              href={entry.uri}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1.5 text-[13px] text-[#2383E2] hover:underline"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              {entry.label || 'Partecipa con Google Meet'}
                            </a>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ Recording Section ═══ */}
              {isGoogleEvent && recordingLoading && (
                <div className="px-5 py-3 flex items-center gap-2 text-xs text-[#787774]">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Caricamento registrazione...
                </div>
              )}

              {isGoogleEvent && recording && (
                <div className="mx-5 my-3 border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-lg overflow-hidden">
                  {/* Recording header */}
                  <div className="px-3 py-2.5 bg-[#F7F6F3] dark:bg-[#ffffff08] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <img src="/icona.png" alt="Flusia" className="h-5 w-5 object-contain rounded-full" />
                      <span className="text-xs font-medium text-[#37352F] dark:text-white">
                        Registrazione Meeting
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusConfig[recording.status]?.color || 'text-gray-600 bg-gray-500/10'}`}>
                      {statusConfig[recording.status]?.label || recording.status}
                    </span>
                  </div>

                  {/* AI Summary — collapsible */}
                  {recording.ai_summary && (
                    <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                      <button
                        onClick={() => setExpandedSection(prev => prev === 'summary' ? null : 'summary')}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] transition-colors"
                      >
                        {expandedSection === 'summary' ? (
                          <ChevronDown className="w-3.5 h-3.5 text-[#787774] flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-[#787774] flex-shrink-0" />
                        )}
                        <Sparkles className="w-3.5 h-3.5 text-purple-500 flex-shrink-0" />
                        <span className="text-xs font-medium text-[#37352F] dark:text-white">
                          Riassunto AI
                        </span>
                      </button>

                      {expandedSection === 'summary' && (
                        <div className="px-3 pb-3 space-y-2">
                          <p className="text-xs text-[#37352F]/80 dark:text-white/80 leading-relaxed whitespace-pre-line">
                            {recording.ai_summary}
                          </p>
                          {recording.ai_key_points.length > 0 && (
                            <div>
                              <h5 className="text-[10px] font-semibold text-[#787774] uppercase tracking-wider mb-1">
                                Punti chiave
                              </h5>
                              <ul className="space-y-0.5">
                                {recording.ai_key_points.map((point, i) => (
                                  <li key={i} className="flex items-start gap-1.5 text-xs text-[#37352F]/80 dark:text-white/80">
                                    <span className="text-purple-500 mt-0.5">-</span>
                                    {point}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {recording.ai_action_items.length > 0 && (
                            <div>
                              <h5 className="text-[10px] font-semibold text-[#787774] uppercase tracking-wider mb-1">
                                Action items
                              </h5>
                              <ul className="space-y-0.5">
                                {recording.ai_action_items.map((item, i) => (
                                  <li key={i} className="text-xs text-[#37352F]/80 dark:text-white/80">
                                    - {item.text}{item.assignee ? ` (${item.assignee})` : ''}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Transcript — collapsible */}
                  {recording.full_transcript && (
                    <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]">
                      <button
                        onClick={() => setExpandedSection(prev => prev === 'transcript' ? null : 'transcript')}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] transition-colors"
                      >
                        {expandedSection === 'transcript' ? (
                          <ChevronDown className="w-3.5 h-3.5 text-[#787774] flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-[#787774] flex-shrink-0" />
                        )}
                        <FileText className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                        <span className="text-xs font-medium text-[#37352F] dark:text-white">
                          Trascrizione
                        </span>
                      </button>

                      {expandedSection === 'transcript' && (
                        <div className="px-3 pb-3 max-h-60 overflow-y-auto">
                          <p className="text-xs text-[#37352F]/80 dark:text-white/80 whitespace-pre-line leading-relaxed">
                            {recording.full_transcript}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] px-3 py-2 flex flex-wrap items-center gap-2">
                    {!recording.full_transcript && recording.status !== 'active' && recording.status !== 'joining' && (
                      <button
                        onClick={handleFetchTranscript}
                        disabled={fetchingTranscript}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors"
                      >
                        {fetchingTranscript ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                        {fetchingTranscript ? 'Recupero...' : 'Recupera Trascrizione'}
                      </button>
                    )}

                    {recording.full_transcript && !recording.ai_summary && (
                      <button
                        onClick={handleGenerateSummary}
                        disabled={generatingSummary}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-purple-600 dark:text-purple-400 hover:text-purple-700 transition-colors"
                      >
                        {generatingSummary ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {generatingSummary ? 'Generazione...' : 'Genera Riassunto AI'}
                      </button>
                    )}

                    <button
                      onClick={() => setShowAssociation(!showAssociation)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium text-[#2383E2] hover:text-[#1a6bc2] transition-colors ml-auto"
                    >
                      <Link2 className="w-3 h-3" />
                      Associa a...
                    </button>
                  </div>

                  {/* Link success message */}
                  {linkSuccess && (
                    <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] px-3 py-2 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                      <span className="text-xs text-green-600 dark:text-green-400">{linkSuccess}</span>
                    </div>
                  )}

                  {/* ═══ Association panel ═══ */}
                  {showAssociation && (
                    <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] p-3 space-y-3">
                      {/* Tabs */}
                      <div className="flex gap-1">
                        {tabConfig.map(tab => (
                          <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                              activeTab === tab.key
                                ? 'bg-[#2383E2] text-white'
                                : 'text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10'
                            }`}
                          >
                            <tab.icon className="w-3 h-3" />
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {/* Search input */}
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#787774]" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={
                            activeTab === 'client' ? 'Cerca cliente...' :
                            activeTab === 'deal' ? 'Cerca deal...' :
                            'Cerca progetto...'
                          }
                          className="w-full pl-8 pr-3 py-1.5 text-xs bg-[#F7F6F3] dark:bg-[#ffffff08] border border-[rgba(55,53,47,0.09)] dark:border-[#3F4447] rounded-md focus:outline-none focus:ring-1 focus:ring-[#2383E2] text-[#37352F] dark:text-white placeholder:text-[#787774]"
                        />
                      </div>

                      {/* Results */}
                      {searchLoading && (
                        <div className="flex items-center gap-1.5 text-xs text-[#787774]">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Ricerca...
                        </div>
                      )}

                      {searchResults.length > 0 && (
                        <div className="max-h-40 overflow-y-auto space-y-0.5">
                          {searchResults.map(result => (
                            <button
                              key={result.id}
                              onClick={() => handleLink(result.id, activeTab)}
                              disabled={linking}
                              className="w-full flex items-center justify-between px-2.5 py-1.5 text-left rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] transition-colors disabled:opacity-50"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-medium text-[#37352F] dark:text-white truncate">
                                  {result.label}
                                </div>
                                {result.sub && (
                                  <div className="text-[10px] text-[#787774] dark:text-gray-400 truncate">
                                    {result.sub}
                                  </div>
                                )}
                              </div>
                              {linking ? (
                                <Loader2 className="w-3 h-3 animate-spin text-[#787774] flex-shrink-0" />
                              ) : (
                                <Link2 className="w-3 h-3 text-[#787774] flex-shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {searchQuery.length >= 2 && !searchLoading && searchResults.length === 0 && (
                        <p className="text-xs text-[#787774] dark:text-gray-400 italic">
                          Nessun risultato
                        </p>
                      )}

                      {activeTab === 'project' && (
                        <p className="text-[10px] text-[#787774] dark:text-gray-400">
                          Associando a un progetto verra creato uno status update con il riassunto AI.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── Description ── */}
              {event.description && (
                <div className="px-5 py-4">
                  <div className="text-[13px] text-[#787774] dark:text-gray-500 mb-2">
                    {t.description || "Descrizione"}
                  </div>
                  {/* Le task commerciali salvano la descrizione in HTML (editor del drawer): va mostrata formattata, non come testo */}
                  {/<[a-z][\s\S]*>/i.test(event.description) ? (
                    <SanitizedHtml html={event.description} className="text-[13px] [&_p]:my-0.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" />
                  ) : (
                    <p className="text-[13px] text-[#37352F] dark:text-[#FFFFFFCF] whitespace-pre-line leading-relaxed">
                      {event.description}
                    </p>
                  )}
                </div>
              )}

              {/* Eventi Google: riprogramma in Flusia (come evento o prenotazione) */}
              {isGoogleEvent && !recording && !recordingLoading && (
                <div className="px-5 py-3 space-y-2">
                  {onRescheduleToFlusia && (
                    <button
                      type="button"
                      onClick={() => { onRescheduleToFlusia(event); }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg bg-[#2383E2] text-white hover:bg-[#1a6fc4] transition-colors"
                    >
                      <Clock className="w-4 h-4" />
                      Riprogramma in Flusia
                    </button>
                  )}
                  <p className="text-xs text-[#787774] dark:text-gray-500 italic">
                    {t.googleCalendarInfo || 'Gli eventi Google Calendar sono di sola lettura. "Riprogramma in Flusia" lo ricrea come evento o prenotazione e rimuove l\'originale.'}
                  </p>
                </div>
              )}
            </div>
          </motion.div>

          {/* ═══ Reschedule Modal (booking) ═══ */}
          {showRescheduleModal && isBookingEvent && (
            <ResponsiveModal
              isOpen={showRescheduleModal}
              onClose={() => { setShowRescheduleModal(false); setRescheduleStep('date'); setRescheduleDate(null); setManualRescheduleTime(''); setManualRescheduleDuration(''); setRescheduleError(null); }}
              title={rescheduleStep === 'date' ? 'Seleziona nuova data' : 'Seleziona nuovo orario'}
              size="lg"
            >
              {rescheduleStep === 'date' && (
                <BookerCalendar
                  selectedDate={rescheduleDate}
                  onChange={handleRescheduleDate}
                  minDate={new Date()}
                />
              )}
              {rescheduleStep === 'time' && (
                <>
                  <button
                    onClick={() => setRescheduleStep('date')}
                    className="flex items-center gap-1 text-sm text-[#787774] hover:text-[#37352F] mb-3 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Cambia data
                  </button>
                  <p className="text-sm text-[#787774] mb-3 capitalize">
                    {rescheduleDate && new Date(rescheduleDate + 'T12:00:00').toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </p>
                  {publicSlotsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 animate-spin text-[#787774]" />
                    </div>
                  ) : (publicAllSlots.length === 0 && publicSlots.length === 0) ? (
                    <div className="text-center py-8 text-[#787774] text-sm">
                      Nessun orario disponibile.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-80 overflow-y-auto">
                      {(publicAllSlots.length > 0 ? publicAllSlots : publicSlots).map((slot) => {
                        const isAvailable = publicSlots.some(s => s.start === slot.start);
                        return (
                          <button
                            key={slot.start}
                            onClick={() => isAvailable ? handleRescheduleSlot(slot) : undefined}
                            disabled={!isAvailable || rescheduling}
                            className={`py-2 text-sm font-medium rounded-lg border transition-all ${
                              isAvailable
                                ? 'border-[rgba(55,53,47,0.16)] hover:bg-[rgba(55,53,47,0.04)] cursor-pointer'
                                : 'border-[rgba(55,53,47,0.06)] bg-[rgba(55,53,47,0.04)] text-[#787774]/50 cursor-not-allowed line-through'
                            }`}
                          >
                            {rescheduling && isAvailable ? (
                              <Loader2 className="w-3 h-3 animate-spin mx-auto" />
                            ) : (
                              new Date(slot.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Appuntamenti esistenti del giorno — accordion collassabile */}
                  {publicDayBookings.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10">
                      <button
                        type="button"
                        onClick={() => setShowDayBookings(v => !v)}
                        className="flex items-center gap-1.5 text-xs text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-colors w-full text-left"
                      >
                        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showDayBookings ? 'rotate-90' : ''}`} />
                        Appuntamenti in agenda ({publicDayBookings.length})
                      </button>
                      {showDayBookings && (
                        <div className="mt-2 space-y-1.5">
                          {publicDayBookings
                            .slice()
                            .sort((a, b) => a.start.localeCompare(b.start))
                            .map((b, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs text-[#37352F] dark:text-white/80 py-1 px-2 rounded-md bg-[rgba(55,53,47,0.03)] dark:bg-white/5">
                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: b.event_type_color }} />
                                <span className="font-medium tabular-nums">
                                  {new Date(b.start).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}–{new Date(b.end).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                <span className="text-[#787774] truncate">{b.booker_name}</span>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Orario + durata manuali: riprogramma a un orario fuori griglia */}
                  {rescheduleDate && !publicSlotsLoading && (() => {
                    const currentDurationMin = event?.start_time && event?.end_time
                      ? Math.max(5, Math.round((new Date(event.end_time).getTime() - new Date(event.start_time).getTime()) / 60000))
                      : 30;
                    // Rilevamento conflitto orario manuale — usa busyIntervals (precisi) o fallback su allSlots
                    const mStart = rescheduleDate && manualRescheduleTime ? new Date(`${rescheduleDate}T${manualRescheduleTime}:00`) : null;
                    const mValid = mStart && !isNaN(mStart.getTime());
                    const mDuration = parseInt(manualRescheduleDuration) || currentDurationMin;
                    const mEnd = mValid ? new Date(mStart!.getTime() + mDuration * 60 * 1000) : null;
                    const busyCheck: Array<{ start: string; end: string }> =
                      publicBusyIntervals.length > 0
                        ? publicBusyIntervals
                        : publicAllSlots.filter(s => s.available === false);
                    const mConflict = (mValid && mEnd)
                      ? busyCheck.find(iv => new Date(iv.start).getTime() < mEnd.getTime() && new Date(iv.end).getTime() > mStart!.getTime())
                      : null;
                    const fmtT = (d: string) => new Date(d).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
                    return (
                    <div className="mt-3 pt-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10">
                      <label className="block text-xs text-[#787774] mb-1.5">Oppure inserisci orario e durata manuali</label>
                      <div className="flex items-end gap-2">
                        <div className="flex-1 min-w-0">
                          <span className="block text-[11px] text-[#787774] mb-1">Ora inizio</span>
                          <input
                            type="time"
                            value={manualRescheduleTime}
                            onChange={(e) => { setManualRescheduleTime(e.target.value); setIgnoreConflict(false); }}
                            className="w-full border border-[rgba(55,53,47,0.16)] dark:border-white/15 rounded-lg px-3 py-2 text-sm bg-white dark:bg-transparent text-[#37352F] dark:text-white"
                          />
                        </div>
                        <div className="w-24 shrink-0">
                          <span className="block text-[11px] text-[#787774] mb-1">Durata (min)</span>
                          <input
                            type="number"
                            min={5}
                            step={5}
                            value={manualRescheduleDuration}
                            placeholder={`${currentDurationMin}`}
                            onChange={(e) => setManualRescheduleDuration(e.target.value)}
                            className="w-full border border-[rgba(55,53,47,0.16)] dark:border-white/15 rounded-lg px-3 py-2 text-sm bg-white dark:bg-transparent text-[#37352F] dark:text-white"
                          />
                        </div>
                        <button
                          type="button"
                          disabled={!manualRescheduleTime || rescheduling || (!!mConflict && !ignoreConflict)}
                          onClick={() => {
                            if (!rescheduleDate || !manualRescheduleTime) return;
                            const start = new Date(`${rescheduleDate}T${manualRescheduleTime}:00`);
                            if (isNaN(start.getTime())) return;
                            const durationMin = parseInt(manualRescheduleDuration) || currentDurationMin;
                            const end = new Date(start.getTime() + durationMin * 60 * 1000);
                            handleRescheduleSlot({ start: start.toISOString(), end: end.toISOString() }, ignoreConflict);
                          }}
                          className="shrink-0 px-3 py-2 text-sm font-medium rounded-lg bg-[#2383E2] text-white hover:bg-[#1a6fc4] disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {rescheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Imposta'}
                        </button>
                      </div>
                      <p className="text-[11px] text-[#787774] mt-1">Per orari fuori dalla griglia. Verrà rifiutato solo se si sovrappone a un altro appuntamento.</p>
                      {mConflict && !ignoreConflict && (
                        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/50 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                          <div className="flex items-start gap-2 mb-2">
                            <span>⚠️</span>
                            <span>
                              L'orario {mValid && fmtT(mStart!.toISOString())} si sovrappone a un appuntamento già esistente ({fmtT(mConflict.start)}–{fmtT(mConflict.end)}).
                            </span>
                          </div>
                          <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={ignoreConflict}
                              onChange={(e) => setIgnoreConflict(e.target.checked)}
                              className="w-3.5 h-3.5 accent-amber-600"
                            />
                            <span>Procedi comunque (ignora conflitto)</span>
                          </label>
                        </div>
                      )}
                      {mConflict && ignoreConflict && (
                        <div className="mt-2 flex items-start gap-2 rounded-lg border border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700/50 px-3 py-2 text-xs text-blue-800 dark:text-blue-300">
                          <span>ℹ️</span>
                          <span>Conflitto ignorato — clicca <strong>Imposta</strong> per confermare la sovrapposizione.</span>
                        </div>
                      )}
                    </div>
                    );
                  })()}

                  {rescheduleError && (
                    <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700/50 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                      <span>⛔</span>
                      <span>{rescheduleError}</span>
                    </div>
                  )}
                </>
              )}
            </ResponsiveModal>
          )}
        </>
      )}
    </AnimatePresence>
  );
}

export default CalendarEventDrawer;
